/**
 * "Nova ficha…" da biblioteca: escolhe o sistema (um tipo de ficha de algum plugin
 * da mesa) e, se quiser, o nome. A ficha nasce na pasta onde o menu foi aberto.
 * A busca só aparece quando há muitos sistemas.
 *
 * O mestre também escolhe o dono (como o "Dono do personagem" do original): ele mesmo, alguém
 * da mesa ou ninguém (só os mestres editam). "Criar uma ficha para…", no menu de um
 * participante, abre este diálogo com o dono já escolhido.
 */
import { useMemo, useState, type KeyboardEvent } from 'react';
import { useT } from '../i18n/index.js';
import { useApp } from '../store.js';
import { Button, ComboBox, Dialog, TextEdit } from '../ui/controls.js';
import { filterSystems, sheetSystems, type SheetSystem } from '../plugins/pluginTypes.js';
import { roomPeople } from './sheetOwners.js';

/** a partir de quantos sistemas o campo de busca aparece */
export const SEARCH_FROM = 6;
/** o dono "ninguém" no seletor (só os mestres editam) */
const NO_OWNER = '';

export function NewSheetDialog({ parentId, onClose, ownerId: presetOwner }: { parentId: string | null; onClose: () => void; ownerId?: string | null }) {
  const t = useT();
  const plugins = useApp((s) => s.plugins);
  const createItem = useApp((s) => s.createLibraryItem);
  const me = useApp((s) => s.user);
  const room = useApp((s) => s.room);
  const users = useApp((s) => s.users);
  const members = useApp((s) => s.members);
  const isGm = !!me && (room?.ownerId === me.id || !!members[me.id]?.modes.includes('gm'));
  const systems = useMemo(() => sheetSystems(plugins), [plugins]);
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [owner, setOwner] = useState<string>(presetOwner === null ? NO_OWNER : (presetOwner ?? me?.id ?? NO_OWNER));
  const withSearch = systems.length >= SEARCH_FROM;
  const shown = withSearch ? filterSystems(systems, query) : systems;
  const people = roomPeople(users, members);
  const ownerOptions = [
    ...people.map((p) => ({ value: p.id, label: p.id === me?.id ? t('library.owner.me', { name: p.name }) : `${p.name} (${t(`users.group.${p.role}.one`)})` })),
    // o dono escolhido pode ter saído da mesa enquanto o diálogo estava aberto
    ...(owner !== NO_OWNER && !people.some((p) => p.id === owner) ? [{ value: owner, label: t('library.owner.away') }] : []),
    { value: NO_OWNER, label: t('library.owner.none') },
  ];
  const forSomeone = presetOwner ? people.find((p) => p.id === presetOwner && p.id !== me?.id) : undefined;

  const create = (system: SheetSystem) => {
    createItem({
      kind: 'sheet',
      name: name.trim() || t('library.newSheet.default'),
      dataType: system.dataType,
      parentId,
      ...(isGm ? { ownerId: owner === NO_OWNER ? null : owner } : {}),
    });
    onClose();
  };
  // Enter cria só quando não há dúvida de qual sistema (sobrou um na lista)
  const onEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (shown.length === 1) create(shown[0]!);
  };

  return (
    <Dialog
      title={forSomeone ? t('library.newSheet.titleFor', { name: forSomeone.name }) : t('library.newSheet.title')}
      onClose={onClose}
      actions={
        <Button variant="secondary" onClick={onClose}>
          {t('create.cancel')}
        </Button>
      }
    >
      <div className="rd-newSheet">
        <TextEdit
          look="filled"
          value={name}
          onChange={setName}
          maxLength={80}
          placeholder={t('library.newSheet.default')}
          aria-label={t('library.newSheet.name')}
          autoFocusOnMount={!withSearch}
          onKeyDown={onEnter}
        />
        {isGm ? (
          <label className="rd-newSheet__owner">
            <span>{t('library.newSheet.owner')}</span>
            <ComboBox value={owner} onChange={setOwner} options={ownerOptions} aria-label={t('library.newSheet.owner')} />
          </label>
        ) : null}
        {withSearch ? (
          <TextEdit look="filled" value={query} onChange={setQuery} placeholder={t('library.newSheet.search')} aria-label={t('library.newSheet.search')} autoFocusOnMount onKeyDown={onEnter} />
        ) : null}
        <ul className="rd-newSheet__list" aria-label={t('library.newSheet.systems')}>
          {shown.map((s) => (
            <li key={`${s.moduleId}:${s.dataType}`}>
              <button type="button" className="rd-newSheet__system" onClick={() => create(s)}>
                <strong>{s.title}</strong>
                {s.plugin !== s.title ? <span>{s.plugin}</span> : null}
              </button>
            </li>
          ))}
          {shown.length === 0 ? <li className="rd-newSheet__none">{t('library.newSheet.none')}</li> : null}
        </ul>
      </div>
    </Dialog>
  );
}
